(() => {
  'use strict';

  const SIZE_PARAM = 'gn_page_size';
  const STORAGE_KEY = 'gn_collection_page_size';
  const PAGE_PARAM = 'gn_page';
  const AVAILABLE_SIZES = new Set([24, 48, 72]);
  const SELECT_SELECTOR = '[data-collection-page-size]';
  const STATE_SELECTOR = '[data-gn-collection-state]';
  const CONTAINER_SELECTOR = '#ProductGridContainer';
  const LIST_SELECTOR = '#ProductsList';
  const STOCK_INDEX_SECTION = 'gn-collection-stock-index';
  const STOCK_INDEX_PAGE_SIZE = 250;
  const STOCK_INDEX_CONCURRENCY = 4;
  const availabilityCache = new Map();
  const nativePageCache = new Map();

  let nativeSnapshot = null;
  let activeRequest = null;
  let requestVersion = 0;
  let infiniteObserver = null;
  let currentMode = 'native';

  const getStoredSize = () => {
    try {
      const value = window.localStorage.getItem(STORAGE_KEY);
      const size = Number(value);
      return value !== null && AVAILABLE_SIZES.has(size) ? size : 24;
    } catch {
      return 24;
    }
  };

  const saveSize = (size) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(size));
    } catch {
      return;
    }
  };

  const getSize = (url = new URL(window.location.href)) => {
    const value = url.searchParams.get(SIZE_PARAM);
    const size = Number(value);
    return value !== null && AVAILABLE_SIZES.has(size) ? size : getStoredSize();
  };

  const mobileViewport = window.matchMedia('(max-width: 767px)');

  // Use the same pagination mode on phones as on desktop (numeric in GetNails).
  const paginationType = (state) => state.pagination;

  const getPage = (url = new URL(window.location.href)) => {
    // Keep old Shopify ?page= links usable after introducing global stock ordering.
    const requested = url.searchParams.get(PAGE_PARAM) || url.searchParams.get('page');
    const page = Number(requested);
    return Number.isSafeInteger(page) && page > 0 ? page : 1;
  };

  const parsePositive = (value, fallback) => {
    const number = Number(value);
    return Number.isSafeInteger(number) && number > 0 ? number : fallback;
  };

  const getElements = () => ({
    container: document.querySelector(CONTAINER_SELECTOR),
    select: document.querySelector(SELECT_SELECTOR),
    status: document.querySelector('[data-gn-page-size-status]'),
  });

  const stopLoading = () => {
    const { container, select } = getElements();
    if (container) container.removeAttribute('aria-busy');
    if (select) select.disabled = false;
  };

  const startLoading = () => {
    const { container, select, status } = getElements();
    if (container) container.setAttribute('aria-busy', 'true');
    if (select) select.disabled = true;
    if (status) {
      status.textContent = '';
      status.classList.add('visually-hidden');
    }
  };

  const cancelPending = () => {
    requestVersion += 1;
    if (activeRequest) activeRequest.abort();
    if (infiniteObserver) infiniteObserver.disconnect();
    activeRequest = null;
    infiniteObserver = null;
    stopLoading();
    return requestVersion;
  };

  const parseNative = (container) => {
    const state = container.querySelector(STATE_SELECTOR);
    const list = container.querySelector(LIST_SELECTOR);
    if (!state || !list) return null;

    return {
      markup: container.innerHTML,
      state: {
        total: Math.max(0, Number(state.dataset.totalCount) || 0),
        nativeSize: parsePositive(state.dataset.nativePageSize, 24),
        sectionId: state.dataset.sectionId,
        pagination: state.dataset.paginationType || 'number',
        mobilePagination: state.dataset.mobilePaginationType || 'number',
        previous: state.dataset.previousLabel || 'Inapoi',
        next: state.dataset.nextLabel || 'Inainte',
        more: state.dataset.moreLabel || 'Show more',
        pager: state.dataset.pagerLabel || 'Product pagination',
        pagerTop: state.dataset.pagerTopLabel || 'Top product pagination',
        pagerBottom: state.dataset.pagerBottomLabel || 'Bottom product pagination',
        pageLabel: state.dataset.pageLabel || 'Page',
        loadError: state.dataset.loadError || 'The products could not be loaded. Please try again.',
      },
      columns: Array.from(list.children),
      products: Array.from(list.children).filter((item) => item.matches('.f-column.card')),
    };
  };

  const captureNative = () => {
    const { container } = getElements();
    if (!container) {
      nativeSnapshot = null;
      return;
    }
    const parsed = parseNative(container);
    if (!parsed) {
      nativeSnapshot = null;
      return;
    }

    const nativePage = parsePositive(new URL(window.location.href).searchParams.get('page'), 1);
    nativeSnapshot = { ...parsed, page: nativePage };
    currentMode = 'native';
  };

  const migratePreviousSelector = () => {
    const url = new URL(window.location.href);
    const previousSize = url.searchParams.get(SIZE_PARAM);
    if (previousSize === null || AVAILABLE_SIZES.has(Number(previousSize))) return;

    url.searchParams.delete(SIZE_PARAM);
    url.searchParams.delete(PAGE_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  };

  const restoreStoredSize = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.has(SIZE_PARAM)) return;

    const savedSize = getStoredSize();
    if (savedSize === 24) return;

    url.searchParams.set(SIZE_PARAM, String(savedSize));
    url.searchParams.delete(PAGE_PARAM);
    url.searchParams.delete('page');
    window.history.replaceState(window.history.state, '', url.toString());
  };

  const syncSelect = () => {
    const { select } = getElements();
    if (select) select.value = String(getSize());
  };

  // A canonical key for the *current* collection, sort, and user-selected filters.
  // Native page number and the 24/48/72 UI selector must not affect this index.
  const collectionKey = () => {
    const url = new URL(window.location.href);
    [SIZE_PARAM, PAGE_PARAM, 'page', 'section_id'].forEach((key) => url.searchParams.delete(key));
    url.hash = '';
    url.searchParams.sort();
    return url.toString();
  };

  const indexForStock = (key, page) => {
    const url = new URL(key);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', STOCK_INDEX_SECTION);
    return url;
  };

  const loadStockIndexPage = async (key, page, signal) => {
    const response = await fetch(indexForStock(key, page).toString(), { signal });
    if (!response.ok) throw new Error(`Stock index request failed (page ${page})`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const stock = doc.querySelector('[data-gn-stock-index]');
    if (!stock) throw new Error('GetNails stock index section is missing');

    const data = {
      page: Number(stock.dataset.page),
      pages: Number(stock.dataset.pages),
      total: Number(stock.dataset.total),
      perPage: Number(stock.dataset.perPage),
      flags: stock.textContent.trim(),
    };
    if (data.page !== page || !Number.isSafeInteger(data.pages) || data.pages < 1 ||
        !Number.isSafeInteger(data.total) || !Number.isSafeInteger(data.perPage) ||
        data.perPage !== STOCK_INDEX_PAGE_SIZE || !/^[01]*$/.test(data.flags)) {
      throw new Error('The Shopify collection stock index is invalid');
    }
    return data;
  };

  const getAvailabilityOrder = async (state, signal) => {
    const key = collectionKey();
    const cached = availabilityCache.get(key);
    if (cached && cached.length === state.total) return cached.order;
    if (state.total === 0) return [];

    const first = await loadStockIndexPage(key, 1, signal);
    if (first.total !== state.total || first.pages !== Math.ceil(state.total / STOCK_INDEX_PAGE_SIZE)) {
      throw new Error('The stock index count differs from the collection count');
    }

    const results = new Array(first.pages);
    results[0] = first;
    let nextPage = 2;

    // Cap requests: small metadata pages, not hundreds of full product cards.
    const worker = async () => {
      while (nextPage <= first.pages) {
        const page = nextPage++;
        results[page - 1] = await loadStockIndexPage(key, page, signal);
      }
    };
    await Promise.all(Array.from({ length: Math.min(STOCK_INDEX_CONCURRENCY, first.pages - 1) }, worker));

    const available = [];
    const unavailable = [];
    let index = 0;
    results.forEach((result, pageIndex) => {
      if (!result || result.total !== first.total || result.pages !== first.pages ||
          result.page !== pageIndex + 1 ||
          result.flags.length !== Math.min(STOCK_INDEX_PAGE_SIZE, state.total - index)) {
        throw new Error('The collection stock index contains incomplete pages');
      }
      for (const flag of result.flags) {
        (flag === '1' ? available : unavailable).push(index++);
      }
    });
    if (index !== state.total) throw new Error('The collection stock index is incomplete');

    const order = available.concat(unavailable);
    // Cache only after every page is validated, and recheck the product count on reuse.
    availabilityCache.set(key, { length: state.total, order });
    if (availabilityCache.size > 12) availabilityCache.delete(availabilityCache.keys().next().value);
    return order;
  };

  const fetchNativePage = async (page, state, signal) => {
    if (nativeSnapshot && nativeSnapshot.page === page) return nativeSnapshot;

    const key = `${collectionKey()}::${state.sectionId}::${state.nativeSize}::${page}`;
    if (nativePageCache.has(key)) return nativePageCache.get(key);

    const url = new URL(window.location.href);
    url.searchParams.delete(SIZE_PARAM);
    url.searchParams.delete(PAGE_PARAM);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', state.sectionId);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error('Collection page request failed');

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const container = doc.querySelector(CONTAINER_SELECTOR);
    if (!container) throw new Error('Shopify did not return the product grid section');

    const result = parseNative(container);
    if (!result || result.state.nativeSize !== state.nativeSize ||
        result.state.total !== state.total) {
      throw new Error('The Shopify product page does not match the current collection');
    }
    nativePageCache.set(key, result);
    if (nativePageCache.size > 35) nativePageCache.delete(nativePageCache.keys().next().value);
    return result;
  };

  const buildUrl = (size, page) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    if (size === 24) url.searchParams.delete(SIZE_PARAM);
    else url.searchParams.set(SIZE_PARAM, String(size));

    if (page === 1) url.searchParams.delete(PAGE_PARAM);
    else url.searchParams.set(PAGE_PARAM, String(page));
    // A meaningful native page fallback for shoppers with JavaScript disabled.
    if (page > 1) url.searchParams.set('page', String(Math.floor((page - 1) * size / 24) + 1));
    return url;
  };

  const element = (tag, className, textContent) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (textContent !== undefined) node.textContent = textContent;
    return node;
  };

  // FoxTheme's <grid-list> animates initial cards by setting inline
  // opacity: 0.01 and visibility: hidden. Cloning an animated card copies
  // those hidden styles, but the existing grid-list will not initialize its
  // animation again for the newly inserted card. Reset only motion styles;
  // preserve all other inline card settings, e.g. CSS custom properties.
  const cloneVisibleColumn = (source) => {
    const column = source.cloneNode(true);
    [column, ...column.querySelectorAll('.product-card')].forEach((item) => {
      item.style.removeProperty('visibility');
      item.style.removeProperty('opacity');
      item.style.removeProperty('transform');
      if (item.getAttribute('style') === '') item.removeAttribute('style');
    });
    return column;
  };

  const makeLink = (size, page, label, options = {}) => {
    const link = element('a', options.className || 'pagination__item', label);
    link.href = buildUrl(size, page).toString();
    link.dataset.gnCollectionPage = String(page);
    if (options.accessibleLabel) link.setAttribute('aria-label', options.accessibleLabel);
    if (options.current) {
      link.classList.add('pagination__item--current', 'background-2');
      link.setAttribute('aria-current', 'page');
      link.removeAttribute('data-gn-collection-page');
    }
    return link;
  };

  // Keep the theme's original caret icon on both pagination bars.
  // Only the icon is visible; the localized direction stays accessible.
  const makeArrowLink = (size, page, label, direction) => {
    const link = makeLink(size, page, undefined, {
      className: `pagination__item pagination__item--${direction} pagination__item-arrow motion-reduce`,
      accessibleLabel: label,
    });
    const namespace = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(namespace, 'svg');
    svg.setAttribute('class', 'icon icon-arrow icon--extra-small');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(namespace, 'path');
    path.setAttribute('d', 'M7.5 3.74121L13.75 9.99121L7.5 16.2412');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    link.appendChild(svg);
    return link;
  };

  // Recreate both pagers together after page-size, filter or stock-order changes.
  const replaceNumericPagination = (container, size, page, pages, state) => {
    container.querySelectorAll('.pagination-wrapper').forEach((item) => item.remove());
    addNumericPagination(container, size, page, pages, state);
  };

  const addNumericPagination = (container, size, page, pages, state) => {
    if (pages <= 1) return;

    const wrapper = element('div', 'pagination-wrapper');
    const nav = element('nav', 'pagination');
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', state.pager);
    const list = element('ul', 'pagination__list list-unstyled flex items-center flex-wrap justify-center');
    list.setAttribute('role', 'list');

    const addItem = (node) => {
      const item = element('li');
      item.appendChild(node);
      list.appendChild(item);
    };

    if (page > 1) addItem(makeArrowLink(size, page - 1, state.previous, 'previous'));

    const pageNumbers = new Set([1, pages]);
    if (mobileViewport.matches && pages > 5) {
      // Compact phone pagination: first/last pages, current page and arrows.
      // Close to either end, keep pages 1-3 or the last 3 visible.
      if (page <= 3) {
        pageNumbers.add(2);
        pageNumbers.add(3);
      } else if (page >= pages - 2) {
        pageNumbers.add(pages - 2);
        pageNumbers.add(pages - 1);
      } else {
        pageNumbers.add(page);
      }
    } else {
      // Desktop keeps its original wider pagination layout.
      for (let index = Math.max(1, page - 2); index <= Math.min(pages, page + 2); index += 1) {
        pageNumbers.add(index);
      }
    }
    let last = 0;
    for (const number of Array.from(pageNumbers).sort((a, b) => a - b)) {
      if (last && number - last > 1) addItem(element('span', 'pagination__item', '...'));
      addItem(makeLink(size, number, String(number), {
        current: number === page,
        accessibleLabel: `${state.pageLabel} ${number}`,
      }));
      last = number;
    }

    if (page < pages) addItem(makeArrowLink(size, page + 1, state.next, 'next'));

    nav.appendChild(list);
    wrapper.appendChild(nav);

    const topHost = container.querySelector('[data-gn-collection-pagination-top]');
    if (topHost) {
      const topWrapper = wrapper.cloneNode(true);
      topWrapper.querySelector('nav').setAttribute('aria-label', state.pagerTop);
      topHost.appendChild(topWrapper);
    }
    nav.setAttribute('aria-label', state.pagerBottom);
    container.appendChild(wrapper);
  };

  const addLoadMorePagination = (container, size, loadedPages, totalPages, state, activePagination) => {
    if (loadedPages >= totalPages) return;

    const wrapper = element('div', 'pagination-wrapper');
    const nav = element('nav', 'pagination text-center');
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', state.pager);
    const button = element('button', 'btn btn--primary', state.more);
    button.type = 'button';
    button.dataset.gnCollectionPage = String(loadedPages + 1);
    nav.appendChild(button);
    wrapper.appendChild(nav);
    container.appendChild(wrapper);

    if (activePagination === 'infinite' && 'IntersectionObserver' in window) {
      infiniteObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          infiniteObserver.disconnect();
          goToPage(loadedPages + 1, false);
        }
      }, { rootMargin: '350px 0px' });
      infiniteObserver.observe(button);
    }
  };

  const goToPage = (page, shouldScroll) => {
    const size = getSize();
    const url = buildUrl(size, page);
    window.history.pushState(window.history.state, '', url.toString());
    renderPage(shouldScroll);
  };

  const renderPage = async (shouldScroll = false) => {
    const version = cancelPending();
    const { container } = getElements();
    if (!container || !nativeSnapshot) return;

    const { state } = nativeSnapshot;
    const size = getSize();
    const activePagination = paginationType(state);
    const useNumeric = activePagination === 'number';
    const totalPages = Math.max(1, Math.ceil(state.total / size));
    const page = Math.min(getPage(), totalPages);

    if (page !== getPage()) {
      window.history.replaceState(window.history.state, '', buildUrl(size, page).toString());
    }
    syncSelect();

    const start = useNumeric ? (page - 1) * size : 0;
    const end = Math.min(state.total, useNumeric ? start + size : page * size);
    if (end === 0) return;

    activeRequest = new AbortController();
    const signal = activeRequest.signal;
    startLoading();

    try {
      // Server-side Shopify paginate always slices BEFORE Liquid can sort products.
      // The 250-product index provides the GLOBAL stock order (across all pages).
      // A failure falls back to Shopify's original order rather than breaking navigation.
      let order;
      try {
        order = await getAvailabilityOrder(state, signal);
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        console.warn('GetNails: global stock order unavailable; using Shopify order.', error);
        order = Array.from({ length: state.total }, (_, index) => index);
      }
      if (requestVersion !== version) return;

      const wanted = order.slice(start, end);

      // Keep the native 24-product first page (and its original animations)
      // when no reorder is needed. For numeric pagination only the first page
      // must match; later links reload and get reordered separately. For
      // load-more / infinite, retain native paging only if ALL products are
      // already in stock-first order, including later native pages.
      if (size === state.nativeSize && page === 1 &&
          nativeSnapshot.page === 1 && currentMode === 'native' &&
          (useNumeric ? wanted.every((index, position) => index === position)
            : order.every((index, position) => index === position))) {
        // Preserve the original visible cards and their entry animations, but
        // synchronize the top and bottom pagers with the active page size.
        if (useNumeric) replaceNumericPagination(container, size, page, totalPages, state);
        return;
      }

      const pageNumbers = new Set(wanted.map((index) => Math.floor(index / state.nativeSize) + 1));
      if (start === 0) pageNumbers.add(1); // Promotion cards appear on the first page only.

      const renderedPages = new Map();
      const toFetch = Array.from(pageNumbers);
      let nextToFetch = 0;
      const worker = async () => {
        while (nextToFetch < toFetch.length) {
          const nativePage = toFetch[nextToFetch++];
          renderedPages.set(nativePage, await fetchNativePage(nativePage, state, signal));
        }
      };
      await Promise.all(Array.from({ length: Math.min(6, toFetch.length) }, worker));
      if (requestVersion !== version) return;

      const nodes = wanted.map((index) => {
        const nativePage = Math.floor(index / state.nativeSize) + 1;
        const nativeIndex = index % state.nativeSize;
        const card = renderedPages.get(nativePage)?.products[nativeIndex];
        if (!card) throw new Error('The collection returned an incomplete product page');
        return cloneVisibleColumn(card);
      });

      if (start === 0) {
        // Preserve original promotional image slots without disturbing product order.
        const firstPage = renderedPages.get(1);
        if (firstPage) {
          firstPage.columns.forEach((column, slot) => {
            if (column.matches('.image-column')) nodes.splice(slot, 0, cloneVisibleColumn(column));
          });
        }
      }

      const list = container.querySelector(LIST_SELECTOR);
      if (!list) throw new Error('The product grid is not available');
      list.replaceChildren(...nodes);

      if (useNumeric) replaceNumericPagination(container, size, page, totalPages, state);
      else {
        container.querySelectorAll('.pagination-wrapper').forEach((item) => item.remove());
        addLoadMorePagination(container, size, page, totalPages, state, activePagination);
      }

      currentMode = 'custom';
      if (typeof list.reload === 'function') list.reload();
      if (shouldScroll) {
        const target = document.querySelector('.collection');
        if (target) {
          const scrollTop = target.getBoundingClientRect().top + window.scrollY - 95;
          window.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        const { status } = getElements();
        if (status) {
          status.textContent = state.loadError;
          status.classList.remove('visually-hidden');
        }
        console.error('GetNails collection pagination:', error);
      }
    } finally {
      if (requestVersion === version) {
        activeRequest = null;
        stopLoading();
      }
    }
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest(SELECT_SELECTOR);
    if (!select) return;
    if (!AVAILABLE_SIZES.has(Number(select.value))) return;

    const size = Number(select.value);
    saveSize(size);
    const url = buildUrl(size, 1);
    window.history.pushState(window.history.state, '', url.toString());
    renderPage();
  });

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-gn-collection-page]');
    if (!link) return;
    const page = parsePositive(link.dataset.gnCollectionPage, 1);
    event.preventDefault();
    goToPage(page, true);
  });

  document.addEventListener('collection:rerendered', () => {
    cancelPending();
    captureNative();
    renderPage();
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target || !event.target.querySelector || !event.target.querySelector(CONTAINER_SELECTOR)) return;
    cancelPending();
    captureNative();
    renderPage();
  });

  window.addEventListener('popstate', () => renderPage());
  mobileViewport.addEventListener('change', () => renderPage());

  const init = () => {
    migratePreviousSelector();
    captureNative();
    restoreStoredSize();
    syncSelect();
    renderPage();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
